import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useAuth } from "src/hooks/useAuth";
import { useLogin } from "src/services/auth/login";
import { setHeaderToken } from "src/services/config/apiClient";
import { getOrgAppsRequest } from "src/services/organizations/queries/apps";
import { getUserSettingsRequest } from "src/services/user/queries/settings";
import { AppType } from "src/types/apps";
import { User } from "src/types/user";
import { PATH_DASHBOARD } from "src/utils/paths";

import { useGetErrorMessage } from "../hooks/useGetErrorMessage";
import { LoginFormValues } from "../hooks/useLoginForm";

export const useLoginService = () => {
	const [isLoading, setIsLoading] = useState(false);

	const { mutate, error } = useLogin();
	const { errorMessage } = useGetErrorMessage(error);

	const { signIn } = useAuth();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const loginService = ({ email, password, remember }: LoginFormValues) => {
		setIsLoading(true);
		mutate(
			{ email: email.toLocaleLowerCase(), password },
			{
				onSuccess: async (data) => {
					setHeaderToken(data.token);

					// Fetch user
					const user: User = await getUserSettingsRequest();
					queryClient.setQueryData(["user"], () => ({ ...user }));

					// Fetch apps
					if (user && user.organization && user.organization.id !== "") {
						const apps: AppType[] = await getOrgAppsRequest(user.organization.id);
						queryClient.setQueryData(["apps"], () => [...apps]);
						const appSelected = queryClient.getQueryData<string>(["apps", "selected"]);
						if (!appSelected || appSelected === "")
							queryClient.setQueryData(["apps", "selected"], apps[0] ? apps[0].id : "");
					}

					signIn(data);

					setTimeout(() => {
						navigate({ to: PATH_DASHBOARD.app.overview, replace: true });
					}, 100);
				},
				onError: () => setIsLoading(false)
			}
		);
	};

	return { loginService, isLoading, errorMessage };
};
